import { createConsumer } from '@rails/actioncable';

const PRESENCE_INTERVAL = 20000;
const RECONNECT_INTERVAL = 1000;

class BaseActionCableConnector {
  static isDisconnected = false;

  constructor(
    app,
    pubsubToken,
    websocketHost = '',
    presenceInterval = PRESENCE_INTERVAL
  ) {
    const websocketURL = websocketHost ? `${websocketHost}/cable` : undefined;

    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        connected: () => {
          this.connectionRecovered();
        },
        disconnected: () => {
          this.connectionLost = true;
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.presenceTimer = null;
    this.reconnectEnabled = true;
    this.connectionLost = false;
    this.isAValidEvent = () => true;
    this.handlePageResume = () => {
      if (document.hidden) return;

      this.ensureConnection();
      this.updatePresenceIfConnected();
    };

    document.addEventListener('visibilitychange', this.handlePageResume);
    window.addEventListener('pageshow', this.handlePageResume);
    window.addEventListener('online', this.handlePageResume);

    this.triggerPresenceInterval = () => {
      this.presenceTimer = setTimeout(() => {
        this.updatePresenceIfConnected();
        this.triggerPresenceInterval();
      }, presenceInterval);
    };
    this.triggerPresenceInterval();
  }

  checkConnection() {
    if (!this.reconnectEnabled) return;

    if (this.consumer.connection.isOpen()) {
      this.connectionRecovered();
      return;
    }

    // ActionCable normally retries by itself, but a backgrounded/discarded
    // tab can leave the consumer inactive without creating another attempt.
    const isConnectionAttempting =
      typeof this.consumer.connection.isActive === 'function' &&
      this.consumer.connection.isActive();
    if (!isConnectionAttempting) this.consumer.connect();
    this.initReconnectTimer();
  }

  ensureConnection = () => {
    if (!this.reconnectEnabled || this.consumer.connection.isOpen()) return;

    this.clearReconnectTimer();
    this.consumer.connect();
    this.initReconnectTimer();
  };

  updatePresenceIfConnected = () => {
    if (!this.reconnectEnabled || !this.consumer.connection.isOpen()) return;

    this.subscription.updatePresence();
  };

  connectionRecovered = () => {
    const shouldNotify = this.connectionLost;
    this.connectionLost = false;
    this.clearReconnectTimer();
    BaseActionCableConnector.isDisconnected = false;
    this.updatePresenceIfConnected();
    if (shouldNotify) this.onReconnect();
  };

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    if (!this.reconnectEnabled) return;

    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {};

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {};

  disconnect() {
    this.reconnectEnabled = false;
    this.clearReconnectTimer();
    if (this.presenceTimer) {
      clearTimeout(this.presenceTimer);
      this.presenceTimer = null;
    }
    document.removeEventListener('visibilitychange', this.handlePageResume);
    window.removeEventListener('pageshow', this.handlePageResume);
    window.removeEventListener('online', this.handlePageResume);
    this.consumer.disconnect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
